import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { numberToDollars } from '../../../../utils'
import { getTaxTypeSentences } from './taxTypeSentences'
import { ArrowGlyph } from '../../../../components/advice/shared'
import {
  DesktopTable,
  HeaderRow,
  RowGroups,
  AdditionalSavings,
  ValueColumnHeading,
  Spacer,
  BalanceGutter,
  PlanSection,
  ValueRow,
  PlanName,
  IraName,
  AmountCell,
  AmountNumber,
  AmountUnit,
  NoChange,
  MatchRow,
  MatchLabel,
  MatchValue,
  TaxSentenceRow,
  TaxSentence,
  MobileCards,
  MobileCard,
  MobilePlanName,
  MobileTaxSentence,
  MobileValueRow,
  MobileAmountColumn,
  MobileAmountValue,
  MobileNoChange,
  MobileArrow,
  MobileMatch,
  MobileDivider,
} from './style'

const formatNumber = value => numberToDollars(value, true)

// Returns separate tokens so the numbers and units can be styled differently.
function getDesktopAmountTokens({ monthly, percent, isPercentOfPay }) {
  if (isPercentOfPay) {
    return [
      { text: formatNumber(percent) },
      { text: '% | $', muted: true },
      { text: formatNumber(monthly) },
      { text: '/month', muted: true },
    ]
  }
  return [
    { text: '$', muted: true },
    { text: formatNumber(monthly) },
    { text: '/month', muted: true },
  ]
}

function DesktopAmount({ tokens, planned }) {
  return tokens.map((token, index) =>
    token.muted ? (
      <AmountUnit key={index} planned={planned}>
        {token.text}
      </AmountUnit>
    ) : (
      <AmountNumber key={index}>{token.text}</AmountNumber>
    )
  )
}

function MobileAmount({ monthly, percent, isPercentOfPay, planned }) {
  return (
    <MobileAmountColumn>
      <MobileAmountValue planned={planned}>{`$${formatNumber(monthly)}/mo`}</MobileAmountValue>
      {isPercentOfPay && (
        <MobileAmountValue planned={planned}>{`(${formatNumber(percent)}%)`}</MobileAmountValue>
      )}
    </MobileAmountColumn>
  )
}

const getPlanTaxInfo = plan =>
  getTaxTypeSentences({ isPercentOfPay: plan.isPercentOfPay, buckets: plan.buckets ?? [] })

const isContributionUnchanged = (plan, hasTaxTypeChange) => {
  if (hasTaxTypeChange) {
    return false
  }
  // Round values to match what the table displays.
  const sameMonthly = Math.round(plan.currentMonthly) === Math.round(plan.recommendedMonthly)
  if (!plan.isPercentOfPay) {
    return sameMonthly
  }
  return sameMonthly && Math.round(plan.currentPercent) === Math.round(plan.recommendedPercent)
}

function ContributionsTable({
  employerPlans,
  iras,
  outOfPlanMonthlySavings,
  isModified,
  isMobile,
}) {
  const hasNonEmployerSavings = iras.length > 0 || outOfPlanMonthlySavings > 0

  if (isMobile) {
    return (
      <MobileCards>
        {employerPlans.map(plan => {
          const { sentences: taxSentences, hasChange: hasTaxTypeChange } = getPlanTaxInfo(plan)
          return (
            <MobileCard key={plan.id}>
              <MobilePlanName>{plan.ownerLabel}</MobilePlanName>
              {taxSentences.length > 0 && (
                <MobileTaxSentence>{taxSentences.join(' ')}</MobileTaxSentence>
              )}
              <MobileValueRow>
                <MobileAmount
                  monthly={plan.currentMonthly}
                  percent={plan.currentPercent}
                  isPercentOfPay={plan.isPercentOfPay}
                />
                <MobileArrow>
                  <ArrowGlyph />
                </MobileArrow>
                {isContributionUnchanged(plan, hasTaxTypeChange) ? (
                  <MobileAmountColumn>
                    <MobileNoChange>no change</MobileNoChange>
                  </MobileAmountColumn>
                ) : (
                  <MobileAmount
                    monthly={plan.recommendedMonthly}
                    percent={plan.recommendedPercent}
                    isPercentOfPay={plan.isPercentOfPay}
                    planned
                  />
                )}
              </MobileValueRow>
              {plan.hasEmployerMatch && (
                <MobileMatch>
                  {plan.isMaxingMatch ? 'This maxes the match' : "This doesn't max the match"}
                </MobileMatch>
              )}
            </MobileCard>
          )
        })}
        {hasNonEmployerSavings && (
          <MobileCard>
            {iras.map((ira, index) => (
              <Fragment key={ira.id}>
                {index > 0 && <MobileDivider />}
                <MobilePlanName>{ira.name}</MobilePlanName>
                <MobileValueRow>
                  <MobileAmount monthly={ira.currentMonthly} isPercentOfPay={false} />
                  <MobileArrow>
                    <ArrowGlyph />
                  </MobileArrow>
                  <MobileAmountColumn>
                    <MobileNoChange>no change</MobileNoChange>
                  </MobileAmountColumn>
                </MobileValueRow>
              </Fragment>
            ))}
            {outOfPlanMonthlySavings > 0 && (
              <Fragment>
                {iras.length > 0 && <MobileDivider />}
                <MobilePlanName>Additional savings</MobilePlanName>
                <MobileValueRow>
                  <MobileAmountColumn />
                  <MobileArrow invisible>
                    <ArrowGlyph />
                  </MobileArrow>
                  <MobileAmount monthly={outOfPlanMonthlySavings} isPercentOfPay={false} planned />
                </MobileValueRow>
              </Fragment>
            )}
          </MobileCard>
        )}
      </MobileCards>
    )
  }

  return (
    <DesktopTable>
      <HeaderRow>
        <BalanceGutter />
        <ValueColumnHeading>CURRENT</ValueColumnHeading>
        <ValueColumnHeading planned>
          {isModified ? 'Your changes' : 'Recommended'}
        </ValueColumnHeading>
        <BalanceGutter />
      </HeaderRow>
      <RowGroups>
        {employerPlans.map(plan => {
          const { sentences: taxSentences, hasChange: hasTaxTypeChange } = getPlanTaxInfo(plan)
          return (
            <PlanSection key={plan.id}>
              <ValueRow>
                <PlanName>{plan.ownerLabel}</PlanName>
                <AmountCell>
                  <DesktopAmount
                    tokens={getDesktopAmountTokens({
                      monthly: plan.currentMonthly,
                      percent: plan.currentPercent,
                      isPercentOfPay: plan.isPercentOfPay,
                    })}
                  />
                </AmountCell>
                <AmountCell planned>
                  {isContributionUnchanged(plan, hasTaxTypeChange) ? (
                    <NoChange>no change</NoChange>
                  ) : (
                    <DesktopAmount
                      planned
                      tokens={getDesktopAmountTokens({
                        monthly: plan.recommendedMonthly,
                        percent: plan.recommendedPercent,
                        isPercentOfPay: plan.isPercentOfPay,
                      })}
                    />
                  )}
                </AmountCell>
                <BalanceGutter />
              </ValueRow>
              {plan.hasEmployerMatch && (
                <MatchRow>
                  <MatchLabel>Maxing the match?</MatchLabel>
                  <MatchValue>{plan.currentIsMaxingMatch ? 'Yes' : 'No'}</MatchValue>
                  <MatchValue planned>{plan.isMaxingMatch ? 'Yes' : 'No'}</MatchValue>
                  <BalanceGutter />
                </MatchRow>
              )}
              {taxSentences.length > 0 && (
                <TaxSentenceRow>
                  <BalanceGutter />
                  <TaxSentence>{taxSentences.join(' ')}</TaxSentence>
                  <BalanceGutter />
                </TaxSentenceRow>
              )}
            </PlanSection>
          )
        })}
        {hasNonEmployerSavings && (
          <AdditionalSavings>
            {iras.map(ira => (
              <ValueRow key={ira.id}>
                <IraName>{ira.name}</IraName>
                <AmountCell>
                  <DesktopAmount
                    tokens={getDesktopAmountTokens({
                      monthly: ira.currentMonthly,
                      isPercentOfPay: false,
                    })}
                  />
                </AmountCell>
                <AmountCell planned>
                  <NoChange>no change</NoChange>
                </AmountCell>
                <BalanceGutter />
              </ValueRow>
            ))}
            {outOfPlanMonthlySavings > 0 && (
              <ValueRow>
                <PlanName>Additional savings</PlanName>
                <Spacer />
                <AmountCell planned>
                  <DesktopAmount
                    planned
                    tokens={getDesktopAmountTokens({
                      monthly: outOfPlanMonthlySavings,
                      isPercentOfPay: false,
                    })}
                  />
                </AmountCell>
                <BalanceGutter />
              </ValueRow>
            )}
          </AdditionalSavings>
        )}
      </RowGroups>
    </DesktopTable>
  )
}

ContributionsTable.propTypes = {
  employerPlans: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      ownerLabel: PropTypes.string,
      isPercentOfPay: PropTypes.bool,
      currentMonthly: PropTypes.number,
      currentPercent: PropTypes.number,
      recommendedMonthly: PropTypes.number,
      recommendedPercent: PropTypes.number,
      hasEmployerMatch: PropTypes.bool,
      isMaxingMatch: PropTypes.bool,
      currentIsMaxingMatch: PropTypes.bool,
      buckets: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          current: PropTypes.number,
          recommended: PropTypes.number,
        })
      ),
    })
  ),
  iras: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      currentMonthly: PropTypes.number,
    })
  ),
  outOfPlanMonthlySavings: PropTypes.number,
  isModified: PropTypes.bool,
  isMobile: PropTypes.bool,
}

export default ContributionsTable
